<template>
  <div style="width: 100%">
    <v-data-table
      v-if="canSeeTable"
      v-model="selectedCompanies"
      :headers="headers"
      :items="companies"
      :search="searchFromParent"
      :page.sync="pagination.page"
      :items-per-page.sync="paginationAssociatedCompanies.rowsPerPage"
      :show-select="selectable"
      @toggle-select-all="selectAll"
      :loading="loading"
      loading-text=""
      :items-per-page-text="rowsPerPageText"
      :items-per-page-items="rowsPerPageItems"
      :header-props="{ sortIcon }"
      :footer-props="{ prevIcon, nextIcon,'items-per-page-options':rowsPerPageItems }"
      class="elevation-1"
      @pagination="setRowsOnLocalStorage(dataTableAssociatedCompaniesId,paginationAssociatedCompanies.rowsPerPage)"
      :class="{
        'select-all-disabled ': !hasPermissionToEdit,
        'no-show-permission': !selectable
      }"
    >
      <template v-slot:item="props">
        <tr @click="viewItem(props.item)">
          <td v-if="selectable" class="tableCheckbox">
            <v-checkbox
              :input-value="companySelected(props.item.uuid)"
              @change="toggleCheckbox(props.item)"
              color="accent"
              hide-details
              :ripple="false"
              :disabled="!hasPermissionToEdit"
              @click.stop
            />
          </td>

          <td class="data-table-name">
            <v-layout row align-center>
              <img :src="props.item.image" class="avatar data-table-logo" />

              <span class="data-table-name data-table-td-content">
                {{ props.item.name }}
              </span>
            </v-layout>
          </td>

          <td>{{ props.item.email }}</td>

          <td>
            <v-layout v-if="isActive(props.item.status)" row align-center>
              <div class="company-status-icon mr-3" />
              <span style="text-transform: lowercase">
                {{ $t("Active") }}
              </span>
            </v-layout>

            <v-layout v-else row align-center>
              <div class="company-status-icon status-inactive mr-3" />
              <span style="text-transform: lowercase">
                {{ $t("Inactive") }}
              </span>
            </v-layout>
          </td>
        </tr>
      </template>

      <template v-slot:pageText="props">
        {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
        {{ props.itemsLength }}
      </template>

      <template v-slot:no-results>
        <data-table-no-results :search="searchFromParent" />
      </template>

      <template v-slot:no-data>
        <div v-if="loading" />
        <v-alert v-else class="data-table-no-data" icon="warning">
          {{ $t("NoDataCompanies") }}
        </v-alert>
      </template>
    </v-data-table>

    <div v-if="!canSeeTable" :class="{ 'page-section': !selectable }">
      <app-no-permission-warning />
    </div>
  </div>
</template>

<script>
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import { companyListsMixin } from "@/mixins/management/companyListsMixin";
import DataTableNoResults from "@/components/_Atoms/DataTableNoResults";
import LockSVG from "@/assets/management/lock.svg";
import AppNoPermissionWarning from "@/components/_Molecules/AppNoPermissionWarning";

export default {
  name: "AssociatedCompanies",

  components: { AppNoPermissionWarning, DataTableNoResults, LockSVG },

  mixins: [managementDatatableMixin, companyListsMixin],

  props: {
    /**
     * Type of action desired when opening the form.
     * @example "edit", "view", "create"
     */
    actionType: {
      type: String,
      required: true
    },

    /**
     * Determine which parent page is using
     * @example "workspace", "user", "role"
     */
    from: {
      type: String
    },

    /**
     * List of associated companies
     */
    associatedCompanies: {
      type: Array
    },

    /**
     * Determines whether the user has permission to edit permissions
     */
    hasPermissionToEdit: {
      Type: Boolean
    }
  },

  data() {
    return {
      /**
       * List of selected companies
       */
      selectedCompanies: [],
      dataTableAssociatedCompaniesId: "dataTableAssociatedCompaniesId" ,
      paginationAssociatedCompanies: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableAssociatedCompaniesId")
      },
    };
  },

  computed: {
    /**
     * Determines if the user can edit or Remove the items.
     * @return {boolean}
     */
    userCanEditOrRemoveItems() {
      return false;
    },

    /**
     * Determines if the user can list the items.
     * @return {boolean}
     */
    canSeeTable() {
      return (
        this.userHasManagementPermission("company_list") &&
        this.userHasManagementPermission(
          this.associationPermissionName + "_list"
        )
      );
    },

    /**
     * Determines the name of the permission dependent on the value of the 'from' variable
     * @returns {string}
     */
    associationPermissionName() {
      if (this.from === "user") {
        return "user_company";
      } else if (this.from === "workspace") {
        return "workspace_company";
      } else if (this.from === "role") {
        return "client_role";
      }
    }
  },

  watch: {
    /**
     * Whenever the variable selectedCompanies is changed,
     * it emits an event with the new value of the variable.
     * @param newSelection
     */
    selectedCompanies(newSelection) {
      this.$emit("associate-companies", newSelection);
    }
  },

  mounted() {
    this.loading = true;
    if (this.canSeeTable) {
      if (this.selectable) {
        this.getAllCompanies();
      } else {
        this.companies = this.associatedCompanies;
        this.loading = false;
      }
      this.selectedCompanies = this.associatedCompanies;
    } else {
      this.loading = false;
      console.log("The user doesn't have the necessary permissions");
    }
  },

  methods: {
    //finds uuid of seleted company and outputs into checkbox input-value
    companySelected(company) {
      let companies = this.selectedCompanies.find(
        item => item.uuid === company
      );
      return companies;
    },
    /**
     * Select or deselect all visible items on the table page
     * @param items
     */
    selectAll(items) {
      if (items.value) {
        this.selectedCompanies = items;
      } else {
        this.selectedCompanies = [];
      }
    },

    /**
     * Select or deselect the item you receive
     * @param item
     */
    toggleCheckbox(company) {
      let idx = this.selectedCompanies.findIndex(
        item => item.id === company.id
      );
      if (idx === -1) this.selectedCompanies.push(company);
      else this.selectedCompanies.splice(idx, 1);
    },

    /**
     * Push to the page to see the selected company
     * @param {Object} item - Selected user
     */
    viewItem(item) {
      if(this.actionType === 'view'){
        if (this.userHasManagementPermission("company_show")) {
          /**
           * @property {String} name - Name of the route
           * @property {Object} params - Parameters needed for the route path
           */
          this.$router.push({ name: "company", params: { id: item.id } });
        } else {
          this.$toasted.clear();
          this.$toasted.global.toast_error(this.$i18n.t("MissingPermissions"));
        }
      }
    }
  }
};
</script>

<style>
.tableCheckbox .v-input--selection-controls__input{
  width: 17px !important;
}
img {
  object-fit: contain;
}
</style>

<style scoped>
.v-data-table >>> .v-data-table__wrapper > table tr td > .text-start,
.v-data-table >>> .v-data-table-header th:first-child {
  margin-right: 10px;
}
.v-data-table >>> th .v-input--selection-controls__input {
  width: 17px !important;
}
.v-data-table >>> .v-input--selection-controls__ripple {
  left: -16px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/SystemManagement/Accounts/AssociatedCompanies.vue